import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { QuestionMediaView } from "@/components/QuestionMedia";

export default function ExamTaker({ exam, onSubmit, submitting, L = (e) => e }) {
  const [ans, setAns] = useState({});
  const dur = exam.duration_min || 0;
  const [remaining, setRemaining] = useState(dur * 60);
  const submittedRef = useRef(false);
  const set = (qid, val) => setAns((a) => ({ ...a, [qid]: val }));
  const toggleMulti = (qid, oi) => setAns((a) => {
    const cur = Array.isArray(a[qid]) ? [...a[qid]] : [];
    const at = cur.indexOf(oi);
    if (at >= 0) cur.splice(at, 1); else cur.push(oi);
    return { ...a, [qid]: cur };
  });
  const setMatch = (qid, idx, val) => setAns((a) => {
    const cur = Array.isArray(a[qid]) ? [...a[qid]] : [];
    cur[idx] = val;
    return { ...a, [qid]: cur };
  });
  const doSubmit = () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    const answers = exam.questions.map((q) => ({ question_id: q.id, answer: ans[q.id] ?? null }));
    onSubmit(answers);
  };
  useEffect(() => {
    if (!dur) return;
    if (remaining <= 0) { doSubmit(); return; }
    const t = setTimeout(() => setRemaining((r) => r - 1), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining, dur]);
  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");
  const submit = doSubmit;
  return (
    <div className="space-y-5">
      {dur > 0 && (
        <div className={`sticky top-2 z-10 flex items-center justify-end`}>
          <span className={`font-heading font-bold px-3 py-1.5 rounded-md border ${remaining <= 30 ? "bg-destructive text-white border-destructive animate-pulse" : "bg-card border-border text-primary"}`} data-testid="exam-timer">
            ⏱ {mm}:{ss}
          </span>
        </div>
      )}
      {exam.questions.map((q, i) => (
        <div key={q.id} className="bg-card border border-border rounded-lg p-5" data-testid={`question-${i}`}>
          <div className="flex justify-between">
            <p className="font-heading font-semibold">{i + 1}. {q.text}</p>
            <span className="text-xs text-muted-foreground shrink-0 ml-3">{q.points} pt</span>
          </div>
          <QuestionMediaView media={q.media} idx={i} />
          <div className="mt-3">
            {q.kind === "mcq" && q.multi && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">{L("Select one or more answers")}</p>
                {q.options.map((opt, oi) => {
                  const sel = Array.isArray(ans[q.id]) && ans[q.id].includes(oi);
                  return (
                    <label key={oi} className={`flex items-center gap-2 border rounded-md p-2.5 cursor-pointer transition-colors ${sel ? "border-primary bg-primary/5" : "border-border"}`} data-testid={`q${i}-opt${oi}`}>
                      <input type="checkbox" checked={sel} onChange={() => toggleMulti(q.id, oi)} />
                      <span className="text-sm">{opt}</span>
                    </label>
                  );
                })}
              </div>
            )}
            {q.kind === "mcq" && !q.multi && (
              <div className="space-y-2">
                {q.options.map((opt, oi) => (
                  <label key={oi} className={`flex items-center gap-2 border rounded-md p-2.5 cursor-pointer transition-colors ${ans[q.id] === oi ? "border-primary bg-primary/5" : "border-border"}`} data-testid={`q${i}-opt${oi}`}>
                    <input type="radio" name={q.id} checked={ans[q.id] === oi} onChange={() => set(q.id, oi)} />
                    <span className="text-sm">{opt}</span>
                  </label>
                ))}
              </div>
            )}
            {q.kind === "truefalse" && (
              <div className="flex gap-2">
                {[["true", true], ["false", false]].map(([lbl, val]) => (
                  <button key={lbl} onClick={() => set(q.id, val)} data-testid={`q${i}-${lbl}`}
                    className={`px-4 py-2 rounded-md text-sm border capitalize ${ans[q.id] === val ? "border-primary bg-primary/5 font-semibold" : "border-border"}`}>
                    {L(lbl === "true" ? "True" : "False")}
                  </button>
                ))}
              </div>
            )}
            {q.kind === "matching" && (
              <div className="space-y-2">
                {q.pairs.map((p, pi) => (
                  <div key={pi} className="flex items-center gap-3">
                    <span className="text-sm w-32">{p.left}</span>
                    <Select value={(ans[q.id] || [])[pi] || ""} onValueChange={(v) => setMatch(q.id, pi, v)}>
                      <SelectTrigger className="flex-1" data-testid={`q${i}-match${pi}`}><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{q.right_options.map((r, ri) => <SelectItem key={ri} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            )}
            {q.kind === "short" && (
              <Textarea value={ans[q.id] || ""} onChange={(e) => set(q.id, e.target.value)} placeholder={L("Your answer")} data-testid={`q${i}-short`} />
            )}
          </div>
        </div>
      ))}
      <Button onClick={submit} disabled={submitting} className="w-full h-11" data-testid="exam-submit-btn">
        {submitting ? L("Submitting…") : L("Submit Exam")}
      </Button>
    </div>
  );
}
